import {
  SKR_SYMBOL,
  formatSkrBaseUnits,
  parseSkrUnitsToBaseUnits,
} from './skr';

export type ChallengeRoundStatus =
  | 'Draft'
  | 'Scheduled'
  | 'Active'
  | 'Closed'
  | 'Revealed'
  | 'ClaimOpen'
  | 'ClaimClosed'
  | 'Archived';

export type ChallengeQuestTier = 'Easy' | 'Deep';

export type ChallengeQuestStatus = 'Draft' | 'Active' | 'Disabled' | 'Archived';

export type ChallengeDeveloperCredentialStatus = 'Active' | 'Revoked';

export const roundStatuses: ChallengeRoundStatus[] = [
  'Draft',
  'Scheduled',
  'Active',
  'Closed',
  'Revealed',
  'ClaimOpen',
  'ClaimClosed',
  'Archived',
];

export const questTiers: ChallengeQuestTier[] = ['Easy', 'Deep'];

export const questStatuses: ChallengeQuestStatus[] = [
  'Draft',
  'Active',
  'Disabled',
  'Archived',
];

export const credentialStatuses: ChallengeDeveloperCredentialStatus[] = [
  'Active',
  'Revoked',
];

/**
 * The ten raw statuses collapse into five learnable semantic tones so a glance
 * at a badge or dot reads as a meaning, not a memorised colour.
 */
export type StatusTone = 'neutral' | 'info' | 'success' | 'warn' | 'danger';

const statusToneMap: Record<string, StatusTone> = {
  Draft: 'neutral',
  Archived: 'neutral',
  Scheduled: 'info',
  Active: 'success',
  ClaimOpen: 'success',
  Closed: 'warn',
  Revealed: 'warn',
  ClaimClosed: 'warn',
  Disabled: 'danger',
  Revoked: 'danger',
};

export function statusTone(status: string | undefined): StatusTone {
  return (status && statusToneMap[status]) || 'neutral';
}

export const statusDotToneClass: Record<StatusTone, string> = {
  neutral: 'bg-neutral-400',
  info: 'bg-blue-400',
  success: 'bg-emerald-400',
  warn: 'bg-amber-400',
  danger: 'bg-rose-400',
};

export const statusBadgeToneClass: Record<StatusTone, string> = {
  neutral:
    'bg-neutral-700/50 text-neutral-300 ring-1 ring-inset ring-neutral-600/40',
  info: 'bg-blue-500/15 text-blue-300 ring-1 ring-inset ring-blue-500/25',
  success:
    'bg-emerald-500/15 text-emerald-300 ring-1 ring-inset ring-emerald-500/25',
  warn: 'bg-amber-500/15 text-amber-300 ring-1 ring-inset ring-amber-500/25',
  danger: 'bg-rose-500/15 text-rose-300 ring-1 ring-inset ring-rose-500/25',
};

export type RoundDateState =
  | 'Unknown'
  | 'Scheduled'
  | 'Open'
  | 'Finalizing'
  | 'Expired'
  | 'Closed';

function parseOptionalTimestampMillis(value: string) {
  const parsed = value.trim() ? Date.parse(value) : Number.NaN;

  return Number.isFinite(parsed) ? parsed : null;
}

export function deriveRoundDateState(
  round: Pick<
    QuestRoundForm,
    'startsAt' | 'endsAt' | 'revealStartsAt' | 'revealEndsAt'
  >,
  now = new Date(),
): RoundDateState {
  const nowMillis = now.getTime();
  const startsAt = parseOptionalTimestampMillis(round.startsAt);
  const endsAt = parseOptionalTimestampMillis(round.endsAt);

  if (
    !Number.isFinite(nowMillis) ||
    startsAt === null ||
    endsAt === null ||
    startsAt >= endsAt
  ) {
    return 'Unknown';
  }

  if (nowMillis < startsAt) {
    return 'Scheduled';
  }

  if (nowMillis < endsAt) {
    return 'Open';
  }

  const revealStartsAt = parseOptionalTimestampMillis(round.revealStartsAt);
  const revealEndsAt = parseOptionalTimestampMillis(round.revealEndsAt);

  if (revealStartsAt !== null && nowMillis < revealStartsAt) {
    return 'Finalizing';
  }

  if (revealEndsAt !== null && nowMillis >= revealEndsAt) {
    return 'Expired';
  }

  return 'Closed';
}

export interface QuestSeasonForm {
  id: string;
  blurb: string;
  claimStartsAt: string;
  claimEndsAt: string;
  rulesUrl: string;
  metadataJson: string;
}

export interface QuestRoundForm {
  id: string;
  seasonId: string;
  roundNumber: string;
  status: ChallengeRoundStatus;
  openBlurb: string;
  openBlurbSubtitle: string;
  closingBlurb: string;
  closingBlurbSubtitle: string;
  completeBlurb: string;
  completeBlurbSubtitle: string;
  startsAt: string;
  endsAt: string;
  displayEndsAt: string;
  eligibilityStartsAt: string;
  eligibilityEndsAt: string;
  revealStartsAt: string;
  revealEndsAt: string;
  upperBoundSkrBaseUnits: string;
  selectionConfigJson: string;
  metadataJson: string;
  disabled: boolean;
}

export interface QuestRoundAppForm {
  id: string;
  roundId: string;
  androidPackage: string;
  appName: string;
  sortOrder: string;
  curationNote: string;
  iconUrl: string;
  deepLinkUrl: string;
  featuredReviewJson: string;
  metadataJson: string;
  disabled: boolean;
  unrevealed: boolean;
}

export interface QuestForm {
  id: string;
  roundAppId: string;
  questKey: string;
  tier: ChallengeQuestTier;
  status: ChallengeQuestStatus;
  title: string;
  description: string;
  // completionCriteria: string;
  // tipsJson: string;
  completionCriteria?: string;
  tipsJson?: string;
  deepLinkUrl: string;
  badgeTitle: string;
  badgeImageUrl: string;
  badgeMetadataJson: string;
  metadataJson: string;
  sortOrder: string;
  disabled: boolean;
}

export interface QuestCredentialForm {
  id: string;
  label: string;
  keyPrefix: string;
  signaturePublicKey: string;
  status: ChallengeDeveloperCredentialStatus;
  scopedAppIds: string[];
  metadataJson: string;
}

export interface QuestSelectionPreviewForm {
  selectedRoundId: string;
  criteriaSnapshotJson: string;
  inputSnapshotJson: string;
  recipientSnapshotJson: string;
  candidateCount: string;
  selectedCount: string;
  tierCode: string;
  amountBaseUnits: string;
  tokenSymbol: string;
  tokenDecimals: string;
}

export interface QuestWorkspaceForm {
  seasons: QuestSeasonForm[];
  rounds: QuestRoundForm[];
  apps: QuestRoundAppForm[];
  quests: QuestForm[];
  credentials: QuestCredentialForm[];
  selectionPreview: QuestSelectionPreviewForm;
}

/**
 * The selection-preview reward amount rides the same "SKR in the form,
 * base units on the wire" convention as the round upper bound: the editor shows
 * SKR while runQuestSelectionPreview/revealQuestAllocations speak base units.
 */
export function selectionPreviewToSkrUnits(
  preview: QuestSelectionPreviewForm,
): QuestSelectionPreviewForm {
  return {
    ...preview,
    amountBaseUnits: formatSkrBaseUnits(preview.amountBaseUnits),
  };
}

export function selectionPreviewToSkrBaseUnits(
  preview: QuestSelectionPreviewForm,
): QuestSelectionPreviewForm {
  const amountBaseUnits = parseSkrUnitsToBaseUnits(preview.amountBaseUnits);

  return {
    ...preview,
    amountBaseUnits: amountBaseUnits ?? preview.amountBaseUnits,
  };
}

export function workspaceToSkrUnits(
  workspace: QuestWorkspaceForm,
): QuestWorkspaceForm {
  return {
    ...workspace,
    rounds: workspace.rounds.map((round) => ({
      ...round,
      upperBoundSkrBaseUnits: formatSkrBaseUnits(round.upperBoundSkrBaseUnits),
    })),
    selectionPreview: selectionPreviewToSkrUnits(workspace.selectionPreview),
  };
}

export function workspaceToSkrBaseUnits(
  workspace: QuestWorkspaceForm,
): QuestWorkspaceForm {
  return {
    ...workspace,
    rounds: workspace.rounds.map((round) => {
      const upperBoundSkrBaseUnits = parseSkrUnitsToBaseUnits(
        round.upperBoundSkrBaseUnits,
      );

      return {
        ...round,
        upperBoundSkrBaseUnits:
          upperBoundSkrBaseUnits ?? round.upperBoundSkrBaseUnits,
      };
    }),
    selectionPreview: selectionPreviewToSkrBaseUnits(
      workspace.selectionPreview,
    ),
  };
}

export type QuestWorkspaceItemKind =
  | 'season'
  | 'round'
  | 'app'
  | 'quest'
  | 'credential';

export type QuestWorkspaceItemRef =
  | { kind: QuestWorkspaceItemKind; id: string }
  | { kind: 'selection' }
  | { kind: 'overview' };

export function questWorkspaceTargetKey(ref: QuestWorkspaceItemRef): string {
  if (ref.kind === 'selection') {
    return 'selection';
  }
  if (ref.kind === 'overview') {
    return 'overview';
  }
  return `${ref.kind}:${ref.id}`;
}

const itemKinds: readonly QuestWorkspaceItemKind[] = [
  'season',
  'round',
  'app',
  'quest',
  'credential',
];

function isItemKind(value: string): value is QuestWorkspaceItemKind {
  return (itemKinds as readonly string[]).includes(value);
}

/**
 * Inverse of {@link questWorkspaceTargetKey}: turns a serialized key (as carried
 * in the `?sel=` URL param) back into a selection ref, or null when the string
 * is not a recognized key. Item ids never contain a colon (local ids are
 * `prefix-<random>`, persisted ids are UUIDs), so splitting on the first colon
 * is unambiguous.
 */
export function parseQuestWorkspaceTargetKey(
  key: string,
): QuestWorkspaceItemRef | null {
  if (key === 'overview') {
    return { kind: 'overview' };
  }
  if (key === 'selection') {
    return { kind: 'selection' };
  }
  const separator = key.indexOf(':');
  if (separator === -1) {
    return null;
  }
  const kind = key.slice(0, separator);
  const id = key.slice(separator + 1);
  if (!id || !isItemKind(kind)) {
    return null;
  }
  return { kind, id };
}

export interface QuestWorkspaceIssue {
  message: string;
  target: QuestWorkspaceItemRef;
  /** The form field key this issue maps to, when it can be tied to one input. */
  field?: string;
  /**
   * True for uniqueness checks that span multiple items. These surface in the
   * issues panel and overview rather than as a single field's inline error.
   */
  crossItem?: boolean;
}

export function seasonDisplayName(_season: QuestSeasonForm, index: number) {
  return `Season ${index + 1}`;
}

export function roundDisplayName(round: QuestRoundForm, index: number) {
  return round.roundNumber
    ? `Round #${round.roundNumber}`
    : `Round ${index + 1}`;
}

const monthNames = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function formatCompactSkrUnits(value: string) {
  const trimmed = value.trim();
  if (!trimmed) {
    return SKR_SYMBOL;
  }

  const amount = Number(trimmed);
  const label =
    Number.isFinite(amount) && amount >= 1000
      ? new Intl.NumberFormat('en-US', {
          notation: 'compact',
          maximumFractionDigits: 2,
        }).format(amount)
      : trimmed;

  return `${label} ${SKR_SYMBOL}`;
}

function parseUtcInput(value: string) {
  const trimmed = value.trim();
  const localMatch =
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(trimmed);

  const date = localMatch
    ? new Date(
        Date.UTC(
          Number(localMatch[1]),
          Number(localMatch[2]) - 1,
          Number(localMatch[3]),
          Number(localMatch[4]),
          Number(localMatch[5]),
          Number(localMatch[6] ?? '0'),
        ),
      )
    : new Date(trimmed);

  return Number.isNaN(date.getTime()) ? null : date;
}

function formatClaimOpenUtc(value: string) {
  const date = parseUtcInput(value);
  if (!date) {
    return 'TBD';
  }

  const hour24 = date.getUTCHours();
  const hour = hour24 % 12 || 12;
  const minutes = date.getUTCMinutes();
  const minuteText = minutes ? `:${String(minutes).padStart(2, '0')}` : '';
  const suffix = hour24 < 12 ? 'AM' : 'PM';

  return `${
    monthNames[date.getUTCMonth()]
  } ${date.getUTCDate()} at ${hour}${minuteText}${suffix} UTC`;
}

export function defaultRoundClosingCopy(
  round: Pick<
    QuestRoundForm,
    'roundNumber' | 'revealStartsAt' | 'upperBoundSkrBaseUnits'
  >,
) {
  const roundNumber = round.roundNumber.trim() || '?';

  return {
    closingBlurb: `${formatCompactSkrUnits(
      round.upperBoundSkrBaseUnits,
    )} drops soon for Round ${roundNumber} players`,
    closingBlurbSubtitle: `Round ${roundNumber} has ended. Claims open ${formatClaimOpenUtc(
      round.revealStartsAt,
    )}`,
  };
}

export function applyDefaultRoundClosingCopy(
  round: QuestRoundForm,
): QuestRoundForm {
  return {
    ...round,
    ...defaultRoundClosingCopy(round),
  };
}

export function updateDefaultRoundClosingCopy(
  round: QuestRoundForm,
  previousRound: QuestRoundForm,
): QuestRoundForm {
  const previous = defaultRoundClosingCopy(previousRound);
  const next = defaultRoundClosingCopy(round);

  return {
    ...round,
    ...(round.closingBlurb === previous.closingBlurb
      ? { closingBlurb: next.closingBlurb }
      : {}),
    ...(round.closingBlurbSubtitle === previous.closingBlurbSubtitle
      ? { closingBlurbSubtitle: next.closingBlurbSubtitle }
      : {}),
  };
}

export function appDisplayName(app: QuestRoundAppForm, index: number) {
  return app.appName || app.androidPackage || `App ${index + 1}`;
}

export function questDisplayName(quest: QuestForm, index: number) {
  return quest.title || quest.questKey || `Quest ${index + 1}`;
}

export function createQuestKeyFromTitle(title: string): string {
  return title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 120)
    .replace(/_+$/g, '');
}

export function credentialDisplayName(
  credential: QuestCredentialForm,
  index: number,
) {
  return credential.keyPrefix || credential.label || `API key ${index + 1}`;
}

export function createSeasonDraft(id: string): QuestSeasonForm {
  return {
    id,
    blurb: '',
    claimStartsAt: '',
    claimEndsAt: '',
    rulesUrl: '',
    metadataJson: '{}',
  };
}

export function createRoundDraft(
  id: string,
  seasonId: string,
  roundNumber: string,
): QuestRoundForm {
  return applyDefaultRoundClosingCopy({
    id,
    seasonId,
    roundNumber,
    status: 'Draft',
    openBlurb: '',
    openBlurbSubtitle: '',
    closingBlurb: '',
    closingBlurbSubtitle: '',
    completeBlurb: '',
    completeBlurbSubtitle: '',
    startsAt: '',
    endsAt: '',
    displayEndsAt: '',
    eligibilityStartsAt: '',
    eligibilityEndsAt: '',
    revealStartsAt: '',
    revealEndsAt: '',
    upperBoundSkrBaseUnits: '',
    selectionConfigJson: '{}',
    metadataJson: '{}',
    disabled: false,
  });
}

export function createAppDraft(
  id: string,
  roundId: string,
  sortOrder: string,
): QuestRoundAppForm {
  return {
    id,
    roundId,
    androidPackage: '',
    appName: '',
    sortOrder,
    curationNote: '',
    iconUrl: '',
    deepLinkUrl: '',
    featuredReviewJson: '',
    metadataJson: '{}',
    disabled: false,
    unrevealed: false,
  };
}

export function createQuestDraft(
  id: string,
  roundAppId: string,
  sortOrder: string,
): QuestForm {
  return {
    id,
    roundAppId,
    questKey: '',
    tier: 'Easy',
    status: 'Active',
    title: '',
    description: '',
    // completionCriteria: '',
    // tipsJson: '[]',
    deepLinkUrl: '',
    badgeTitle: '',
    badgeImageUrl: '',
    badgeMetadataJson: '{}',
    metadataJson: '{}',
    sortOrder,
    disabled: false,
  };
}

export function createCredentialDraft(
  id: string,
  appId = '',
): QuestCredentialForm {
  return {
    id,
    label: '',
    keyPrefix: '',
    signaturePublicKey: '',
    status: 'Active',
    scopedAppIds: appId ? [appId] : [],
    metadataJson: '{}',
  };
}

export function createInitialQuestWorkspace(): QuestWorkspaceForm {
  const season = createSeasonDraft('season-local-1');
  const round = createRoundDraft('round-local-1', season.id, '1');

  return {
    seasons: [season],
    rounds: [round],
    apps: [createAppDraft('app-local-1', 'round-local-1', '0')],
    quests: [createQuestDraft('quest-local-1', 'app-local-1', '0')],
    credentials: [createCredentialDraft('credential-local-1', 'app-local-1')],
    selectionPreview: {
      selectedRoundId: 'round-local-1',
      criteriaSnapshotJson: '{}',
      inputSnapshotJson: '',
      recipientSnapshotJson: '',
      candidateCount: '',
      selectedCount: '',
      tierCode: '',
      amountBaseUnits: '',
      tokenSymbol: 'SKR',
      tokenDecimals: '9',
    },
  };
}

function hasValue(value: string) {
  return value.trim().length > 0;
}

function isValidJson(value: string, allowEmpty = false) {
  if (!hasValue(value)) {
    return allowEmpty;
  }

  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
}

function pushRequired(
  issues: QuestWorkspaceIssue[],
  target: QuestWorkspaceItemRef,
  value: string,
  label: string,
  field?: string,
) {
  if (!hasValue(value)) {
    issues.push({ message: `${label} is required.`, target, field });
  }
}

function pushInteger(
  issues: QuestWorkspaceIssue[],
  target: QuestWorkspaceItemRef,
  value: string,
  label: string,
  field?: string,
) {
  if (hasValue(value) && !/^-?\d+$/.test(value.trim())) {
    issues.push({ message: `${label} must be an integer.`, target, field });
  }
}

function pushInvalidJson(
  issues: QuestWorkspaceIssue[],
  target: QuestWorkspaceItemRef,
  value: string,
  label: string,
  allowEmpty = false,
  field?: string,
) {
  if (!isValidJson(value, allowEmpty)) {
    issues.push({ message: `${label} must be valid JSON.`, target, field });
  }
}

function pushDateOrder(
  issues: QuestWorkspaceIssue[],
  target: QuestWorkspaceItemRef,
  start: string,
  end: string,
  label: string,
  field?: string,
) {
  if (
    hasValue(start) &&
    hasValue(end) &&
    Date.parse(start) >= Date.parse(end)
  ) {
    issues.push({
      message: `${label} start must be before its end.`,
      target,
      field,
    });
  }
}

function pushDuplicateValues(
  issues: QuestWorkspaceIssue[],
  entries: Array<{
    value: string;
    target: QuestWorkspaceItemRef;
    field?: string;
  }>,
  label: string,
) {
  const seen = new Set<string>();
  const duplicates = new Map<
    string,
    { target: QuestWorkspaceItemRef; field?: string }
  >();

  for (const entry of entries) {
    const normalizedValue = entry.value.trim().toLowerCase();
    if (!normalizedValue) {
      continue;
    }

    if (seen.has(normalizedValue)) {
      const trimmedValue = entry.value.trim();
      if (!duplicates.has(trimmedValue)) {
        duplicates.set(trimmedValue, {
          target: entry.target,
          field: entry.field,
        });
      }
      continue;
    }

    seen.add(normalizedValue);
  }

  for (const [duplicate, { target, field }] of duplicates) {
    issues.push({
      message: `${label} "${duplicate}" must be unique.`,
      target,
      field,
      crossItem: true,
    });
  }
}

function pushDuplicateScopedValues(
  issues: QuestWorkspaceIssue[],
  entries: Array<{
    scope: string;
    value: string;
    target: QuestWorkspaceItemRef;
    field?: string;
  }>,
  label: string,
) {
  const seen = new Set<string>();
  const duplicates = new Map<
    string,
    { value: string; target: QuestWorkspaceItemRef; field?: string }
  >();

  for (const entry of entries) {
    const normalizedScope = entry.scope.trim().toLowerCase();
    const normalizedValue = entry.value.trim().toLowerCase();
    if (!normalizedScope || !normalizedValue) {
      continue;
    }

    const scopedValue = `${normalizedScope}:${normalizedValue}`;
    if (seen.has(scopedValue)) {
      if (!duplicates.has(scopedValue)) {
        duplicates.set(scopedValue, {
          value: entry.value.trim(),
          target: entry.target,
          field: entry.field,
        });
      }
      continue;
    }

    seen.add(scopedValue);
  }

  for (const { value, target, field } of duplicates.values()) {
    issues.push({
      message: `${label} "${value}" must be unique.`,
      target,
      field,
      crossItem: true,
    });
  }
}

export function validateQuestWorkspaceDetailed(
  workspace: QuestWorkspaceForm,
): QuestWorkspaceIssue[] {
  const issues: QuestWorkspaceIssue[] = [];

  for (const season of workspace.seasons) {
    const target: QuestWorkspaceItemRef = { kind: 'season', id: season.id };
    const label = seasonDisplayName(
      season,
      workspace.seasons.findIndex((item) => item.id === season.id),
    );
    pushInvalidJson(
      issues,
      target,
      season.metadataJson,
      `${label} metadata JSON`,
      false,
      'metadataJson',
    );
    pushDateOrder(
      issues,
      target,
      season.claimStartsAt,
      season.claimEndsAt,
      `${label} claim window`,
      'claimStartsAt',
    );
  }

  for (const round of workspace.rounds) {
    const target: QuestWorkspaceItemRef = { kind: 'round', id: round.id };
    const label = roundDisplayName(
      round,
      workspace.rounds.findIndex((item) => item.id === round.id),
    );
    pushRequired(
      issues,
      target,
      round.roundNumber,
      `${label} number`,
      'roundNumber',
    );
    pushInteger(
      issues,
      target,
      round.roundNumber,
      `${label} number`,
      'roundNumber',
    );
    pushRequired(
      issues,
      target,
      round.startsAt,
      `${label} start date`,
      'startsAt',
    );
    pushRequired(issues, target, round.endsAt, `${label} end date`, 'endsAt');
    if (
      hasValue(round.upperBoundSkrBaseUnits) &&
      parseSkrUnitsToBaseUnits(round.upperBoundSkrBaseUnits) === null
    ) {
      issues.push({
        message: `${label} upper bound SKR must be a non-negative amount with up to 9 decimals.`,
        target,
        field: 'upperBoundSkrBaseUnits',
      });
    }
    pushInvalidJson(
      issues,
      target,
      round.selectionConfigJson,
      `${label} selection config JSON`,
      false,
      'selectionConfigJson',
    );
    pushInvalidJson(
      issues,
      target,
      round.metadataJson,
      `${label} metadata JSON`,
      false,
      'metadataJson',
    );
    pushDateOrder(
      issues,
      target,
      round.startsAt,
      round.endsAt,
      `${label} round`,
      'startsAt',
    );
    if (
      hasValue(round.eligibilityStartsAt) ||
      hasValue(round.eligibilityEndsAt)
    ) {
      pushDateOrder(
        issues,
        target,
        hasValue(round.eligibilityStartsAt)
          ? round.eligibilityStartsAt
          : round.startsAt,
        hasValue(round.eligibilityEndsAt)
          ? round.eligibilityEndsAt
          : round.endsAt,
        `${label} eligibility window`,
        'eligibilityStartsAt',
      );
    }
    if (hasValue(round.revealStartsAt) || hasValue(round.revealEndsAt)) {
      if (hasValue(round.revealEndsAt)) {
        pushRequired(
          issues,
          target,
          round.revealStartsAt,
          `${label} reveal start date`,
          'revealStartsAt',
        );
      }
      pushDateOrder(
        issues,
        target,
        round.revealStartsAt,
        round.revealEndsAt,
        `${label} reveal window`,
        'revealStartsAt',
      );
    }
  }

  for (const app of workspace.apps) {
    const target: QuestWorkspaceItemRef = { kind: 'app', id: app.id };
    const label = app.appName || app.androidPackage || 'Round app';
    pushRequired(
      issues,
      target,
      app.androidPackage,
      `${label} androidPackage`,
      'androidPackage',
    );
    pushRequired(issues, target, app.appName, `${label} appName`, 'appName');
    pushRequired(
      issues,
      target,
      app.curationNote,
      `${label} curationNote`,
      'curationNote',
    );
    pushInvalidJson(
      issues,
      target,
      app.featuredReviewJson,
      `${label} featuredReview JSON`,
      true,
      'featuredReviewJson',
    );
    pushInvalidJson(
      issues,
      target,
      app.metadataJson,
      `${label} metadata JSON`,
      false,
      'metadataJson',
    );
  }

  for (const quest of workspace.quests) {
    const target: QuestWorkspaceItemRef = { kind: 'quest', id: quest.id };
    const label = quest.title || quest.questKey || 'Quest';
    pushRequired(
      issues,
      target,
      quest.questKey,
      `${label} questKey`,
      'questKey',
    );
    pushRequired(issues, target, quest.title, `${label} title`, 'title');
    pushRequired(
      issues,
      target,
      quest.description,
      `${label} description`,
      'description',
    );
    // pushRequired(
    //   issues,
    //   target,
    //   quest.completionCriteria,
    //   `${label} completionCriteria`,
    //   'completionCriteria',
    // );
    // pushInvalidJson(
    //   issues,
    //   target,
    //   quest.tipsJson,
    //   `${label} tips JSON`,
    //   false,
    //   'tipsJson',
    // );
    pushInvalidJson(
      issues,
      target,
      quest.badgeMetadataJson,
      `${label} badge metadata JSON`,
      false,
      'badgeMetadataJson',
    );
    pushInvalidJson(
      issues,
      target,
      quest.metadataJson,
      `${label} metadata JSON`,
      false,
      'metadataJson',
    );
  }

  for (const credential of workspace.credentials) {
    const target: QuestWorkspaceItemRef = {
      kind: 'credential',
      id: credential.id,
    };
    pushInvalidJson(
      issues,
      target,
      credential.metadataJson,
      'Credential metadata JSON',
      false,
      'metadataJson',
    );
  }

  const selectionTarget: QuestWorkspaceItemRef = { kind: 'selection' };
  if (
    workspace.selectionPreview.selectedRoundId &&
    !workspace.rounds.some(
      (round) => round.id === workspace.selectionPreview.selectedRoundId,
    )
  ) {
    issues.push({
      message: 'Selection preview round must exist.',
      target: selectionTarget,
    });
  }
  pushInvalidJson(
    issues,
    selectionTarget,
    workspace.selectionPreview.criteriaSnapshotJson,
    'Selection criteria snapshot JSON',
    false,
    'criteriaSnapshotJson',
  );
  pushInvalidJson(
    issues,
    selectionTarget,
    workspace.selectionPreview.inputSnapshotJson,
    'Selection input snapshot JSON',
    true,
    'inputSnapshotJson',
  );
  pushInvalidJson(
    issues,
    selectionTarget,
    workspace.selectionPreview.recipientSnapshotJson,
    'Selection recipient snapshot JSON',
    true,
    'recipientSnapshotJson',
  );

  pushDuplicateScopedValues(
    issues,
    workspace.rounds.map((round) => ({
      scope: round.seasonId,
      value: round.roundNumber,
      target: { kind: 'round', id: round.id },
      field: 'roundNumber',
    })),
    'Round number',
  );
  pushDuplicateScopedValues(
    issues,
    workspace.apps.map((app) => ({
      scope: app.roundId,
      value: app.androidPackage,
      target: { kind: 'app', id: app.id },
      field: 'androidPackage',
    })),
    'Android package',
  );
  pushDuplicateValues(
    issues,
    workspace.quests.map((quest) => ({
      value: quest.questKey,
      target: { kind: 'quest', id: quest.id },
      field: 'questKey',
    })),
    'Quest key',
  );
  pushDuplicateScopedValues(
    issues,
    workspace.quests.map((quest) => ({
      scope: quest.roundAppId,
      value: quest.tier,
      target: { kind: 'quest', id: quest.id },
      field: 'tier',
    })),
    'Quest tier',
  );
  pushDuplicateValues(
    issues,
    workspace.credentials.map((credential) => ({
      value: credential.keyPrefix,
      target: { kind: 'credential', id: credential.id },
      field: 'keyPrefix',
    })),
    'Credential key prefix',
  );

  return issues;
}

export function validateQuestWorkspace(
  workspace: QuestWorkspaceForm,
): string[] {
  return validateQuestWorkspaceDetailed(workspace).map(
    (issue) => issue.message,
  );
}
